import { Box, Card, CardContent, Divider, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useEffect, useRef, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { OfferCluster } from '../types/search.types';
import { SearchFilters } from '../types/search.types';
import { AppButton } from '@/shared/components';
import { formatPriceWithAbbr } from '@/shared/utils/formatters';

interface MapMarkerPopupProps {
  readonly cluster: OfferCluster;
  /** Pixel position of the marker tip, relative to the position:relative container */
  readonly anchorPx: { x: number; y: number };
  /** Ref to the outer container Box (position: relative) for width measurement */
  readonly containerRef: RefObject<HTMLDivElement | null>;
  readonly onClose: () => void;
  /** Search filters passed through to offer detail for back-navigation */
  readonly searchFilters?: SearchFilters;
}

function formatDistance(distance?: number): string {
  if (distance === undefined || distance === null) return '–';
  return `${distance.toFixed(1)} km`;
}

export function MapMarkerPopup({
  cluster,
  anchorPx,
  containerRef,
  onClose,
  searchFilters,
}: Readonly<MapMarkerPopupProps>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const popupRef = useRef<HTMLDivElement>(null);

  // Close on Escape key (AC-019.25)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Focus trap: move focus into popup on mount for accessibility
  useEffect(() => {
    popupRef.current?.focus();
  }, []);

  const handleViewDetails = (offer: OfferCluster['offers'][number]) => {
    onClose();
    // Pass the full offer object so OfferDetailPage can use pre-calculated
    // distance and availabilityDuration without an extra API round-trip.
    navigate(`/offers/${offer.id}`, {
      state: {
        from: 'map',
        searchFilters,
        offerData: offer,
      },
    });
  };

  // Popup is always centered horizontally on the marker and shown BELOW the pin tip.
  // anchorPx.x = canvas center x in outerBox coords (marker is centered after camera pan)
  // anchorPx.y = canvas center y in outerBox coords (pin tip is at this y coordinate)
  const containerWidth = containerRef.current?.offsetWidth ?? 400;
  const containerHeight = containerRef.current?.offsetHeight ?? 700;
  const popupWidth = Math.min(300, containerWidth - 24);

  // Center horizontally on the marker, clamped inside the container
  const rawLeft = anchorPx.x - popupWidth / 2;
  const clampedLeft = Math.max(8, Math.min(rawLeft, containerWidth - popupWidth - 8));

  // Popup top = 8px below pin tip
  const popupTop = anchorPx.y - 20;
  // Max height: reach at most 12px from the container bottom so nothing is cut off
  const popupMaxHeight = Math.max(300, containerHeight - popupTop - 12);

  const anchoredStyle = {
    position: 'absolute' as const,
    top: popupTop,
    left: clampedLeft,
    zIndex: 20,
    maxHeight: popupMaxHeight,
    overflow: 'hidden',
  };

  const isSingle = cluster.offers.length === 1;

  return (
    <Card
      ref={popupRef}
      tabIndex={-1}
      data-testid="map-marker-popup"
      onClick={e => e.stopPropagation()} // AC-019.23
      sx={{
        width: popupWidth,
        maxWidth: popupWidth,
        boxShadow: 4,
        borderRadius: '12px',
        outline: 'none',
        display: 'flex',
        flexDirection: 'column',
        ...anchoredStyle,
      }}
    >
      {isSingle ? (
        <SingleOfferContent
          cluster={cluster}
          onClose={onClose}
          onViewDetails={handleViewDetails}
          t={t}
        />
      ) : (
        <ClusterContent
          cluster={cluster}
          onClose={onClose}
          onViewDetails={handleViewDetails}
          t={t}
        />
      )}
    </Card>
  );
}

// ─── Shared offer card content (single offer) ─────────────────────────────────

interface ContentProps {
  cluster: OfferCluster;
  onClose: () => void;
  onViewDetails: (offer: OfferCluster['offers'][number]) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

interface OfferCardContentProps {
  offer: OfferCluster['offers'][number];
  onClose?: () => void;
  onViewDetails: (offer: OfferCluster['offers'][number]) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
  /** data-testid suffix for the view-details button */
  testIdSuffix?: string;
}

function OfferCardContent({
  offer,
  onClose,
  onViewDetails,
  t,
  testIdSuffix,
}: Readonly<OfferCardContentProps>) {
  const product = offer.products[0];
  const productName = product?.material?.subcategory ?? product?.name ?? '';
  const quantityLabel =
    product != null
      ? `${product.quantity} ${product.unit?.labelFr ?? product.unit?.labelEn ?? ''}`
      : t('search.map.popup.quantityNotSpecified');

  const availabilityLabel =
    offer.availabilityDuration != null
      ? t('search.map.popup.weeks_other', { count: offer.availabilityDuration })
      : '–';

  const unitPriceLabel =
    product?.unitPrice != null
      ? `${formatPriceWithAbbr(product.unitPrice)} / ${
          product.unit?.labelFr ?? product.unit?.labelEn ?? ''
        }`
      : null;

  return (
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      {/* Header row: title + optional close */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          data-testid="map-popup-title"
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.3,
          }}
        >
          {offer.title}
        </Typography>
        {onClose && (
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t('search.map.popup.close')}
            data-testid="map-popup-close"
            sx={{ mt: -0.5, mr: -0.5, flexShrink: 0 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Address */}
      {productName && (
        <Typography
          variant="caption"
          fontWeight={600}
          data-testid="map-popup-product-name"
          sx={{ display: 'block', mb: 0.5, lineHeight: 1.3 }}
        >
          {productName}
        </Typography>
      )}

      {/* Address */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1.5 }}>
        <LocationOnIcon sx={{ fontSize: 14, color: 'text.secondary', mt: '1px', flexShrink: 0 }} />
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="map-popup-address"
          sx={{ lineHeight: 1.3 }}
        >
          {offer.address}
        </Typography>
      </Box>

      {/* Info rows */}
      <InfoRow label={t('search.map.popup.quantity')} value={quantityLabel} />
      <InfoRow label={t('search.map.popup.availability')} value={availabilityLabel} />
      <InfoRow label={t('search.map.popup.distance')} value={formatDistance(offer.distance)} />
      {unitPriceLabel != null && (
        <Box data-testid="map-popup-unit-price">
          <InfoRow label={t('search.map.popup.unitPrice')} value={unitPriceLabel} />
        </Box>
      )}

      <AppButton
        variant="primary"
        fullWidth
        data-testid={
          testIdSuffix ? `map-popup-view-details-${testIdSuffix}` : 'map-popup-view-details'
        }
        onClick={() => onViewDetails(offer)}
        sx={{ mt: 1 }}
      >
        {t('search.map.popup.viewDetails')}
      </AppButton>
    </CardContent>
  );
}

// ─── Single-offer layout ───────────────────────────────────────────────────────

function SingleOfferContent({ cluster, onClose, onViewDetails, t }: Readonly<ContentProps>) {
  return (
    <OfferCardContent
      offer={cluster.offers[0]}
      onClose={onClose}
      onViewDetails={onViewDetails}
      t={t}
    />
  );
}

// ─── Cluster layout ────────────────────────────────────────────────────────────

function ClusterContent({ cluster, onClose, onViewDetails, t }: Readonly<ContentProps>) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
      {/* Sticky header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} data-testid="map-popup-cluster-header">
          {t('search.map.popup.offersAtLocation', { count: cluster.offers.length })}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label={t('search.map.popup.close')}
          data-testid="map-popup-close"
          sx={{ mr: -0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Scrollable list: each item uses the same card layout as single-offer */}
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {cluster.offers.map((offer, idx) => (
          <Box key={offer.id} data-testid="map-popup-cluster-item">
            <OfferCardContent
              offer={offer}
              onViewDetails={onViewDetails}
              t={t}
              testIdSuffix={offer.id}
            />
            {idx < cluster.offers.length - 1 && <Divider />}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ─── Shared info row ───────────────────────────────────────────────────────────

function InfoRow({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="caption" fontWeight={500}>
        {value}
      </Typography>
    </Box>
  );
}
